import { useState, useCallback } from "react";
import { X, Search, Loader2, CheckCircle2, AlertTriangle, Trash2, ChevronLeft } from "lucide-react";
import type { MedicineOption, MedicineEntry } from "../../services/api/admin-order.types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";

interface MedicineModalProps {
  onClose: () => void;
  onSave: (medicines: MedicineEntry[]) => void;
  initialMedicines?: MedicineEntry[];
}

const MedicineModal: React.FC<MedicineModalProps> = ({ onClose, onSave, initialMedicines = [] }) => {
  const [medicines, setMedicines] = useState<MedicineEntry[]>(initialMedicines);
  const [step, setStep] = useState<"search" | "dosage" | "edit">("search");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<MedicineOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState<MedicineOption | null>(null);
  const [dosageText, setDosageText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [editGeneratedText, setEditGeneratedText] = useState("");
  const [editNote, setEditNote] = useState("");
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback(
    (text: string) => {
      setSearch(text);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (!text.trim()) { setResults([]); return; }
      const timer = setTimeout(async () => {
        try {
          setSearching(true);
          const response = await AdminOrderManagementService.searchMedicines(text);
          if (response.success) setResults(response.data);
        } catch (error) {
          console.error("Medicine search error:", error);
        } finally {
          setSearching(false);
        }
      }, 400);
      setDebounceTimer(timer);
    },
    [debounceTimer]
  );

  const selectMedicine = (med: MedicineOption) => {
    setSelectedMedicine(med);
    setDosageText("");
    setNoteText("");
    setStep("dosage");
    setSearch("");
    setResults([]);
  };

  const saveMedicine = async () => {
    if (!selectedMedicine || !dosageText.trim()) return;
    const newEntry: MedicineEntry = {
      id: selectedMedicine.id,
      medicineName: selectedMedicine.name,
      medicineId: selectedMedicine.id,
      medicineSalt: selectedMedicine.salt,
      productForm: selectedMedicine.productForm,
      originalText: dosageText,
      generatedText: "",
      dosageType: "",
      missing: [],
      note: noteText,
      isFormatting: true,
      sequenceNo: medicines.length + 1,
    };
    const updatedMedicines = [...medicines, newEntry];
    setMedicines(updatedMedicines);
    setStep("search");
    setSelectedMedicine(null);
    setDosageText("");
    setNoteText("");

    // Format in background
    try {
      const response = await AdminOrderManagementService.generateDosageText(dosageText);
      if (response.success) {
        setMedicines((prev) =>
          prev.map((m) =>
            m.medicineId === newEntry.medicineId && m.sequenceNo === newEntry.sequenceNo
              ? { ...m, generatedText: response.data.dosage, dosageType: response.data.product_type, missing: response.data.missing, isFormatting: false }
              : m
          )
        );
      }
    } catch {
      setMedicines((prev) =>
        prev.map((m) =>
          m.medicineId === newEntry.medicineId && m.sequenceNo === newEntry.sequenceNo
            ? { ...m, isFormatting: false, generatedText: dosageText }
            : m
        )
      );
    }
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setEditGeneratedText(medicines[index].generatedText);
    setEditNote(medicines[index].note);
    setStep("edit");
  };

  const saveEdit = async () => {
    const med = medicines[editingIndex];
    const textChanged = editGeneratedText !== med.generatedText;
    const updated = [...medicines];
    updated[editingIndex] = { ...med, note: editNote };

    if (textChanged) {
      updated[editingIndex].isFormatting = true;
      updated[editingIndex].generatedText = editGeneratedText;
      setMedicines(updated);
      setStep("search");
      setEditingIndex(-1);
      try {
        const response = await AdminOrderManagementService.generateDosageText(editGeneratedText);
        if (response.success) {
          setMedicines((prev) =>
            prev.map((m, i) =>
              i === editingIndex
                ? { ...m, generatedText: response.data.dosage, dosageType: response.data.product_type, missing: response.data.missing, isFormatting: false }
                : m
            )
          );
        }
      } catch {
        setMedicines((prev) =>
          prev.map((m, i) => (i === editingIndex ? { ...m, isFormatting: false } : m))
        );
      }
    } else {
      // Just remove missing fields if no edit
      updated[editingIndex] = { ...updated[editingIndex], missing: [] };
      setMedicines(updated);
      setStep("search");
      setEditingIndex(-1);
    }
  };

  const removeMedicine = (index: number) => {
    if (confirm("Are you sure you want to remove this medicine?")) {
      setMedicines((prev) => prev.filter((_, i) => i !== index));
      setStep("search");
      setEditingIndex(-1);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-[600px] w-[95%] max-h-[85vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-800">
            {step === "dosage" ? "Add Dosage Details" : step === "edit" ? "Edit Medicine" : "Add Medicines"}
          </h3>
          <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Medicine header list (always visible when medicines exist) */}
        {medicines.length > 0 && (
          <div className="px-5 pt-3 pb-2 border-b border-slate-100">
            <div className="text-[0.65rem] font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Added Medicines ({medicines.length})
            </div>
            <div className="max-h-[130px] overflow-y-auto space-y-1.5">
              {medicines.map((med, i) => (
                <div
                  key={`${med.medicineId}-${i}`}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                    editingIndex === i ? "border-indigo-300 bg-indigo-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                  onClick={() => { if (step !== "edit") openEdit(i); }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">{med.medicineName}</div>
                    <div className="text-[0.6rem] text-slate-400 truncate">
                      {med.isFormatting ? "Formatting..." : med.generatedText || med.originalText}
                    </div>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    {med.isFormatting ? (
                      <Loader2 size={14} className="animate-spin text-amber-500" />
                    ) : med.missing.length === 0 ? (
                      <CheckCircle2 size={14} className="text-emerald-500" />
                    ) : (
                      <div className="flex items-center gap-1">
                        <AlertTriangle size={14} className="text-amber-500" />
                        <span className="text-[0.55rem] text-amber-600 font-medium">{med.missing.join(", ")}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {/* STEP: Search */}
          {step === "search" && (
            <>
              <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search medicine by name..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                />
              </div>
              {searching ? (
                <div className="py-6 text-center text-xs text-slate-400">Searching...</div>
              ) : results.length > 0 ? (
                <div className="space-y-1">
                  {results.map((med) => (
                    <div
                      key={med.id}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-emerald-50 cursor-pointer transition-colors border border-transparent hover:border-emerald-200"
                      onClick={() => selectMedicine(med)}
                    >
                      <div>
                        <div className="text-xs font-semibold text-slate-800">{med.name}</div>
                        <div className="text-[0.6rem] text-slate-400">{med.salt} {med.productForm && `· ${med.productForm}`}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : search.trim() ? (
                <div className="py-6 text-center text-xs text-slate-400">No medicines found</div>
              ) : null}
            </>
          )}

          {/* STEP: Dosage */}
          {step === "dosage" && selectedMedicine && (
            <div className="space-y-4">
              <button
                className="flex items-center gap-1 text-xs text-slate-500 font-semibold hover:text-slate-700 bg-transparent border-none cursor-pointer mb-2"
                onClick={() => setStep("search")}
              >
                <ChevronLeft size={14} /> Back to search
              </button>
              <div>
                <div className="text-[0.65rem] font-semibold text-slate-400 uppercase mb-1">Medicine</div>
                <div className="px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="text-xs font-bold text-slate-800">{selectedMedicine.name}</div>
                  <div className="text-[0.6rem] text-slate-400">{selectedMedicine.salt}</div>
                </div>
              </div>
              <div>
                <label className="text-[0.65rem] font-semibold text-slate-500 uppercase block mb-1">Dosage Details *</label>
                <textarea
                  value={dosageText}
                  onChange={(e) => setDosageText(e.target.value)}
                  placeholder="e.g. 1 tablet twice daily after food for 5 days"
                  className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-20 transition-all"
                />
              </div>
              <div>
                <label className="text-[0.65rem] font-semibold text-slate-500 uppercase block mb-1">Note (optional)</label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Any additional notes..."
                  className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-16 transition-all"
                />
              </div>
              <div className="flex justify-end">
                <button
                  className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors border-none cursor-pointer disabled:opacity-40"
                  disabled={!dosageText.trim()}
                  onClick={saveMedicine}
                >
                  Save Medicine
                </button>
              </div>
            </div>
          )}

          {/* STEP: Edit */}
          {step === "edit" && editingIndex >= 0 && medicines[editingIndex] && (
            <div className="space-y-4">
              <button
                className="flex items-center gap-1 text-xs text-slate-500 font-semibold hover:text-slate-700 bg-transparent border-none cursor-pointer mb-2"
                onClick={() => { setStep("search"); setEditingIndex(-1); }}
              >
                <ChevronLeft size={14} /> Back
              </button>
              {medicines[editingIndex].missing && medicines[editingIndex].missing.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2 text-amber-800 text-xs">
                  <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Missing Details: </span>
                    <span>{medicines[editingIndex].missing.join(", ")}</span>
                  </div>
                </div>
              )}
              <div>
                <div className="text-[0.65rem] font-semibold text-slate-400 uppercase mb-1">Medicine Name</div>
                <div className="px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600">
                  {medicines[editingIndex].medicineName}
                </div>
              </div>
              <div>
                <div className="text-[0.65rem] font-semibold text-slate-400 uppercase mb-1">Written Text</div>
                <div className="px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600">
                  {medicines[editingIndex].originalText}
                </div>
              </div>
              <div>
                <label className="text-[0.65rem] font-semibold text-slate-500 uppercase block mb-1">Generated Text (editable)</label>
                <textarea
                  value={editGeneratedText}
                  onChange={(e) => setEditGeneratedText(e.target.value)}
                  className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-20 transition-all"
                />
              </div>
              <div>
                <label className="text-[0.65rem] font-semibold text-slate-500 uppercase block mb-1">Note (editable)</label>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  className="w-full px-3 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none h-16 transition-all"
                />
              </div>
              <div className="flex justify-between">
                <button
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition-colors cursor-pointer"
                  onClick={() => removeMedicine(editingIndex)}
                >
                  <Trash2 size={12} /> Remove Medicine
                </button>
                <button
                  className="px-4 py-2 rounded-lg text-xs font-semibold bg-indigo-500 text-white hover:bg-indigo-600 transition-colors border-none cursor-pointer"
                  onClick={saveEdit}
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "search" && (
          <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
            <button
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-indigo-500 text-white hover:bg-indigo-600 transition-colors border-none cursor-pointer disabled:opacity-40"
              disabled={medicines.length === 0}
              onClick={() => onSave(medicines)}
            >
              Done ({medicines.length} medicine{medicines.length !== 1 ? "s" : ""})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicineModal;
